import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, Slide, IconButton } from '@mui/material';
import { Close as CloseIcon } from '../icons';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const SlideTransition = (props) => {
  return <Slide {...props} direction="up" />;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, severity = 'info', options = {}) => {
    const {
      duration = severity === 'error' ? 8000 : 4000,
      action = null,
      persist = false,
      id = Date.now() + Math.random()
    } = options;

    const toast = {
      id,
      message,
      severity,
      duration,
      action,
      persist,
      open: true,
    };

    setToasts(prev => [...prev, toast]);

    if (!persist && duration > 0) {
      setTimeout(() => {
        hideToast(id);
      }, duration);
    }

    return id;
  }, []);

  const hideToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const hideAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const success = useCallback((message, options = {}) => {
    return showToast(message, 'success', options);
  }, [showToast]);

  const error = useCallback((message, options = {}) => {
    return showToast(message, 'error', options);
  }, [showToast]);

  const warning = useCallback((message, options = {}) => {
    return showToast(message, 'warning', options);
  }, [showToast]);

  const info = useCallback((message, options = {}) => {
    return showToast(message, 'info', options);
  }, [showToast]);

  const loading = useCallback((message = 'Loading...', options = {}) => {
    return showToast(message, 'info', { persist: true, ...options });
  }, [showToast]);

  const value = {
    showToast,
    hideToast,
    hideAllToasts,
    success,
    error,
    warning,
    info,
    loading
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.map((toast) => (
        <Snackbar
          key={toast.id}
          open={toast.open}
          autoHideDuration={toast.persist ? null : toast.duration}
          onClose={() => hideToast(toast.id)}
          TransitionComponent={SlideTransition}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            '& .MuiSnackbar-root': {
              position: 'relative',
            },
            mb: toasts.indexOf(toast) * 1,
          }}
        >
          <Alert
            onClose={() => hideToast(toast.id)}
            severity={toast.severity}
            variant="filled"
            action={
              toast.action || (
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => hideToast(toast.id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )
            }
            sx={{
              width: '100%',
              minWidth: '300px',
              maxWidth: '500px',
              '& .MuiAlert-message': {
                fontSize: '0.875rem',
                fontWeight: 500,
              },
              '& .MuiAlert-icon': {
                fontSize: '1.25rem',
              },
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      ))}
    </ToastContext.Provider>
  );
}; 